import { CommonModule } from '@angular/common';
import { Component, computed, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageTabModeService } from 'app/core/services/page-tab-mode.service';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { DirectivesModule } from 'app/shared/directives/directives.module';
import { TranslocoService } from '@ngneat/transloco';
import { filter } from 'rxjs';


@Component({
  selector: 'aep-page-mode-tab',
  imports: [
    CommonModule,
    DirectivesModule
  ],
  templateUrl: './page-mode-tab.component.html',
  styleUrl: './page-mode-tab.component.scss',
  /* animations: [
    trigger('indicatorAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleX(0)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', 
          style({ opacity: 1, transform: 'scaleX(1)' })
        )
      ])
    ])
  ] */
    animations: [
      trigger('indicatorAnimation', [
        state('void', style({
          opacity: 0,
          transform: 'scale(0.7)'
        })),
        state('*', style({
          opacity: 1,
          transform: 'scale(1)'
        })),
        transition('void => *', [
          animate('225ms cubic-bezier(0.4, 0, 0.2, 1)')
        ]),
        transition('* => void', [
          animate('195ms cubic-bezier(0.4, 0, 0.2, 1)')
        ])
      ])
    ]

})
export class PageModeTabComponent {
  /**
   * Bumped whenever the active language changes or a lazy scope finishes loading, so the
   * labels get re-translated. The transloco pipe can't do it here: it caches the first
   * resolved value per key, and this component lives in the layout, outside the lazy
   * modules that provide TRANSLOCO_SCOPE, so that first value is often the raw key.
   */
  private _translationVersion = signal(0);

  private _requestedScopes = new Set<string>();

  /** Tabs with their label already resolved against the current language. */
  tabs = computed(() => {
    this._translationVersion();

    return this.pageTabModeService.tabs().map((tab) => ({
      ...tab,
      displayLabel: this._resolveLabel(tab.labelKey, tab.label),
    }));
  });

  constructor(
    public pageTabModeService: PageTabModeService,
    private _translocoService: TranslocoService,
  ) {
    this._translocoService.events$
      .pipe(
        filter((event) => event.type === 'translationLoadSuccess'),
        takeUntilDestroyed(),
      )
      .subscribe(() => this._translationVersion.update((version) => version + 1));

    this._translocoService.langChanges$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._requestedScopes.clear();
        this._translationVersion.update((version) => version + 1);
        this._loadTabScopes();
      });

    effect(() => {
      // Re-runs whenever a page publishes a new set of tabs.
      this.pageTabModeService.tabs();
      this._loadTabScopes();
    });
  }

  /** Loads the translation scopes the current tabs need, if they aren't loaded yet. */
  private _loadTabScopes(): void {
    const lang = this._translocoService.getActiveLang();

    for (const tab of this.pageTabModeService.tabs()) {
      if (!tab.scope) {
        continue;
      }

      const scopedLang = `${tab.scope}/${lang}`;
      if (this._requestedScopes.has(scopedLang)) {
        continue;
      }
      this._requestedScopes.add(scopedLang);

      this._translocoService.load(scopedLang).subscribe({
        error: () => this._requestedScopes.delete(scopedLang),
      });
    }
  }

  /** Falls back to the hardcoded label while the translation isn't available yet. */
  private _resolveLabel(labelKey: string | undefined, label: string): string {
    if (!labelKey) {
      return label;
    }

    const translation = this._translocoService.translate(labelKey);

    return !translation || translation === labelKey ? (label || labelKey) : translation;
  }
}
